import { useEffect, useState, useMemo, Fragment } from "react";
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Container,
  TextField,
  Typography,
  Modal,
  Box,
  CircularProgress,
  Grid,
} from "@pankod/refine-mui";
import { useOne, useResource } from "@pankod/refine-core";

import axios from "utils/axios";
import { translate } from "strapiDataProvider";
import useLocales from "locales/useLocales";
import { ExtractData, ExtractCouponData } from "./ExtractData";
import moment from "moment";

import CouponRow from "./CouponAvailabilities";
import AccommodationRow from "./AccommodationAvailabilities";
import CustomDatePicker from "components/DatePickerMUI";

import {
  bbStyle,
  bb1Style,
  bb2Style,
  bb3Style,
  btStyle,
  bt1Style,
  bt2Style,
  bt3Style,
} from "./BorderStyles";

function formatDate(date) {
  // Define arrays for days and months
  var daysOfWeek = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  var monthsOfYear = [
    "Gen",
    "Feb",
    "Mar",
    "Apr",
    "Mag",
    "Giu",
    "Lug",
    "Ago",
    "Set",
    "Ott",
    "Nov",
    "Dic",
  ];

  // Get the day, number of the day, and month
  var dayOfWeek = daysOfWeek[date.getDay()];
  var dayOfMonth = date.getDate();
  var month = monthsOfYear[date.getMonth()];

  return dayOfWeek + " " + dayOfMonth.toString().padStart(2, "0") + " " + month;
}

export default function Planner() {
  const [loading, setLoading] = useState(true);
  const [hotelID, setHotelID] = useState();
  const [hotelName, setHotelName] = useState();
  const [hotelCoupons, setHotelCoupons] = useState([]);
  const [availabilitiesData, setAvailabilitiesData] = useState();
  const [rows, setRows] = useState();
  const [shownCoupons, setShownCoupons] = useState([]);

  const { defaultLang, currentLang } = useLocales();

  var { id } = useResource();
  const thisData = useOne({
    resource: "strutture",
    id,
    metaData: {
      populate: {
        accommodations: {
          populate: {
            localizations: true,
            coupons: true,
            rate_plans: {
              populate: "localizations",
            },
          },
        },
        localizations: true,
        coupons: true,
      },
      fakeParam: currentLang.value,
    },
  });

  const [shownDays, setShownDays] = useState(
    Array(14)
      .fill(new Date())
      .map((item, index) => {
        var day = new Date(item);
        day.setDate(item.getDate() + index);
        return day;
      })
  );

  useEffect(() => {
    if (thisData.data) {
      //get the id of the locale "en"
      const locale = thisData.data.data.locale;
      if (locale != defaultLang) {
        const localizations = thisData.data.data.localizations;
        const index = localizations
          .map((loc) => loc.locale)
          .indexOf(defaultLang);
        if (index >= 0) {
          id = localizations[index].id;
        }
      }
      setHotelID(id);
      setHotelName(thisData.data.data.businessName);
      setHotelCoupons(thisData.data.data.coupons);
    }
  }, [thisData]);

  useEffect(() => {
    //filter visible coupons in the range of selected date
    if (!loading) {
      if (hotelCoupons.length > 0) {
        console.log("Coupons", hotelCoupons, shownDays);
        let filteredCoupons = hotelCoupons.filter((coupon) => {
          let startCoupon = moment(coupon.start);
          let endCoupon = moment(coupon.end);
          let startDay = moment(shownDays[0]);
          let endDay = moment(shownDays[13]);
          return (
            (startCoupon.isSameOrBefore(endDay) &&
              endCoupon.isSameOrAfter(startDay)) ||
            (startCoupon.isSameOrBefore(startDay) &&
              endCoupon.isSameOrAfter(endDay))
          );
        });

        //forEach Coupon in the selected range of date fill in the availabilities
        for (let i = 0; i < filteredCoupons.length; i++) {
          filteredCoupons[i].accommodations = ExtractCouponData(
            availabilitiesData,
            filteredCoupons[i],
            shownDays[0]
          );
        }
        console.log(filteredCoupons);
        setShownCoupons(filteredCoupons);
      }
    }
  }, [shownDays, hotelCoupons, loading]);

  const changeCalendar = (offset, targetDate) => {
    const firstDay = targetDate ? new Date(targetDate) : shownDays[0];

    const tmp = Array(14)
      .fill(firstDay)
      .map((item, index) => {
        var day = new Date(item);
        day.setDate(item.getDate() + index + offset);
        return day;
      });

    setShownDays(tmp);
  };

  const nextWeek = () => {
    changeCalendar(7);
  };

  const prevWeek = () => {
    changeCalendar(-7);
  };

  //aggiorna i parametri per la ricerca disponibilità
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      if (hotelID) {
        //per come è fatto il backend va richiesto un giorno in più
        const endDate = new Date(shownDays[shownDays.length - 1]);
        endDate.setDate(endDate.getDate() + 1);
        //fetch data
        const res = await axios.get(
          `/api/availability?start=${shownDays[0].toISOString()}&end=${endDate.toISOString()}&hotelID=${hotelID}`
        );

        //console.log(res.data);
        setAvailabilitiesData(res.data);
        setRows(ExtractData(res.data));
        setLoading(false);
      }
    }
    fetchData();
  }, [shownDays, hotelID, currentLang]);

  return (
    <>
      {loading && (
        <Modal open={loading}>
          <Box
            sx={{
              width: "100%",
              position: "absolute",
              top: "50%",
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        </Modal>
      )}

      <TableContainer component={Paper} sx={{ p: 2 }}>
        <Grid container justifyContent="flex-start" my={3}>
          <Typography variant="h5">{hotelName}</Typography>
        </Grid>
        <Container
          sx={{
            display: "flex",
            width: 1,
            justifyContent: "space-between",
            pt: 1,
            pb: 2,
          }}
        >
          <Button
            disableElevation
            variant="contained"
            onClick={() => prevWeek()}
          >
            Settimana precedente
          </Button>
          {/* <TextField
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            label="Seleziona data"
            type="date"
            onChange={(e) => changeCalendar(0, e.target.value)}
          /> */}
          <CustomDatePicker
            value={shownDays[0]}
            onChange={(e) => changeCalendar(0, e)}
            label="Seleziona data"
            disablePast={false}
            /*disableDay={(day) => {
              return day < new Date("2024-01-15");
            }}*/
          />
          <Button
            disableElevation
            variant="contained"
            onClick={() => nextWeek()}
          >
            Settimana successiva
          </Button>
        </Container>
        <Table stickyHeader aria-label="collapsible table" padding="none">
          <TableHead style={{ height: 50 }}>
            <TableRow
              sx={{
                "& th": {
                  borderBottom: "unset",
                  ...bt3Style,
                },
              }}
            >
              <TableCell />
              <TableCell />
              <TableCell />
              {shownDays.map((day, index) => (
                <TableCell key={index} align="center">
                  <Typography variant="body">{formatDate(day)}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              "& tr:first-of-type td": bt3Style,
            }}
          >
            {rows &&
              rows.map((row) => (
                <AccommodationRow
                  key={row.accommodationName}
                  row={row}
                  firstDay={shownDays[0]}
                  loading={loading}
                />
              ))}

            {/* COUPON */}
            {shownCoupons.length > 0 &&
              shownCoupons.map((coupon) => (
                <Fragment key={coupon.id}>
                  <TableRow
                    sx={{
                      "& td": {
                        borderBottom: "unset",
                        ...bb3Style,
                        py: 2,
                      },
                    }}
                  >
                    <TableCell colSpan={17}>
                      <Typography
                        variant="h6"
                        sx={{
                          bgcolor: "#eee",
                          py: 2,
                          px: 4,
                        }}
                      >
                        Disponibilità Coupon - {coupon.name}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {coupon.accommodations.map((row) => (
                    <CouponRow
                      key={row.accommodationName}
                      row={row}
                      couponID={coupon.id}
                      firstDay={shownDays[0]}
                      loading={loading}
                    />
                  ))}
                </Fragment>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
